import amplitude from 'amplitude-js';

const client = amplitude.getInstance('wwfMain');

// Name conventions for Amplitude charting
const paymentName = {
  'wwf_purchasetokenise': 'CC',
  'wwf_directdebit': 'DD',
  'wwf_paypal': 'Paypal'
};

// Helper functions required for specific pages/products
function isImpactProductPage() {
  return !!document.getElementById('impact-product-header');
}
function isChampionsPage() {
  return !!document.getElementById('amplitude-ra-champions');
}
function isHubPage() {
  return !!document.getElementById("amplitude-ra-hub");
}
function isLeafReliefPage() {
  return !!document.getElementById("amplitude-leaf-relief");
}

function checkAdoptionType(product) {
  // Check if adoption product is within native, international or ecommerce page
  const isEcommerce = product.extrasetting && product.extrasetting.isEcommerce;
  const isInternational = window.location.href.includes("international");

  if (isEcommerce) return "single";
  if (isInternational) return "international";
  return "native";
}

function checkFilterPage() {
  // Check what page the ecommerce filter is used on
  const isAdoption = window.location.href.includes("adopt");
  const isWildcard = window.location.href.includes("wildcard");

  if (isAdoption) return "Adoption";
  if (isWildcard) return "Wildcard";
  return "Regenerator store";
}

const state = {
  sessionId: null,
  capturedEvents: [],
};

const getters = {
  amplitudeId: (state) => state.sessionId,
};

const mutations = {
  setSessionId: function (state, sessionId) {
    state.sessionId = sessionId;
  },
  pushCapturedEvent: function (state, event) {
    state.capturedEvents.push(event);
  },
  clearCapturedEvents: function (state) {
    state.capturedEvents = [];
  },
};

const actions = {
  initAmplitude({ getters, dispatch, commit }) {
    try {
      const apiKey = wwfconfigs.AMPLITUDE_API_KEY;
      const userId = getters.ssoId;
      const options = {
        includeReferrer: true, 
        includeUtm: true,
        includeGclid: true,
        saveParamsReferrerOncePerSession: false,
      };

      client.init(apiKey, userId, options);

      commit('setSessionId', client.getSessionId());
      dispatch('sendCapturedEvents');
    } catch (err) {
      console.error(err);
    }
  },

  captureEvent({ commit }, { eventName, eventProperties }) {
    try {
      // Shape - { timestamp, eventName, eventProperties }
      const eventDetails = { timestamp: Date.now(), eventName, eventProperties };
      commit('pushCapturedEvent', eventDetails);
    } catch (err) {
      console.error(err);
    }
  },

  sendCapturedEvents({ state, commit }) {
    try {
      state.capturedEvents.forEach((event) => {
        client.logEventWithTimestamp(event.eventName, event.eventProperties, event.timestamp);
      });
      commit('clearCapturedEvents');
    } catch (err) {
      console.error(err);
    }
  },

  // AMPLITUDE PAGE VISIT ACTIONS

  logLandingVisit({ getters, dispatch }, id) {
    try {
      const { donation } = getters;
      const eventName = `${id} page viewed`;
      const eventProperties = {
        'Source': document.referrer,
        'Page name': id,
        'Page Path': window.location.pathname,
        'Page URL': window.location.href,
      };

      // Don't log if processing paypal payment
      if (donation.isProcessing || window.location.href.includes("PayerID")) return;
      
      // Capture event if ssoId or amplitude not ready, will be sent once amplitude is initialized with ssoId
      if (!getters.ssoId || !getters.amplitudeId) {
        dispatch('captureEvent', { eventName, eventProperties });
        return;
      }
      client.logEvent(eventName, eventProperties);
    } catch (err) {
      console.error(err);
    }
  },

  logProductVisit({ getters, dispatch }) {
    try {
      const { product, donation } = getters;
      const eventName = isImpactProductPage() ? 'project viewed' : 'product viewed';
      const eventProperties = {
        'Product ID': product.id,
        'Source': document.referrer,
      };

      // Don't log if processing paypal payment
      if (donation.isProcessing || window.location.href.includes("PayerID")) return;

      // Champions page doesn't require this log
      if (isChampionsPage()) return;

      // Capture event if ssoId or amplitude not ready, will be sent once amplitude is initialized with ssoId
      if (!getters.ssoId || !getters.amplitudeId) {
        dispatch('captureEvent', { eventName, eventProperties });
        return;
      }

      client.logEvent(eventName, eventProperties);
    } catch (err) {
      console.error(err);
    }
  },

  // AMPLITUDE ECOMMERCE ACTIONS

  logProductFilter(context, filter) {
    try {
      const eventProperties = {
        'Page name': checkFilterPage(),
        'Filter Type': filter,
      };
      client.logEvent('product filtered', eventProperties);
    } catch (err) {
      console.error(err);
    }
  },

  // AMPLITUDE FORM FLOW ACTIONS

  logFormBegin({ getters, dispatch }, fieldName)  {
    try {
      const { product, donation } = getters;
      let eventName = 'donation form started';
      const eventProperties = {
        'Product ID': product.id,
        'Donation Type': donation.type,
        'Amount': donation.amount,
        'Field': fieldName
      };

      if (product.producttype == 'wildcard') {
        eventName = 'wildcard form started';
      }

      if (product.producttype == 'adoption') {
        const type = checkAdoptionType(product);
        eventName = `${type} adoption form started`;
      }

      if (isImpactProductPage()) {
        eventName = 'impact project form started';
      }

      // Capture event if ssoId or amplitude not ready, will be sent once amplitude is initialized with ssoId
      if (!getters.ssoId || !getters.amplitudeId) {
        dispatch('captureEvent', { eventName, eventProperties });
        return;
      }

      client.logEvent(eventName, eventProperties);
    } catch (err) {
      console.error(err);
    }
  },

  logFormProduct({ getters })  {
    try {
      const { product, donation } = getters;
      const eventProperties = { 
        'Donation Type': donation.type,
        'Product ID': product.id,
        'Payment Type': paymentName[donation.payementType],
        'Amount': donation.amount
      };

      if (product.producttype == 'wildcard') {
        Object.assign(eventProperties, {
          'Occasion ID': product.productname,
          'Design ID': donation.packagecode,
        });
      }

      if (product.producttype == 'adoption') {
        Object.assign(eventProperties, {
          'Species name': product.productname,
          'Pack ID': donation.packagecode,
          'Adoption as Gift': donation.isGift ? 'yes' : 'no'
        });
      }

      // Attach extra info on user for Amplitude charting
      client.setUserProperties({
        'Amount': donation.amount,
        'Donation Type': donation.type,
        'Product Type': product.producttype
      });
      
      client.logEvent('product selected', eventProperties);
    } catch (err) {
      console.error(err);
    }
  },

  logFormRecipient({ getters })  {
    try {
      const { product, donation } = getters;
      const eventProperties = { 'Product ID': product.id };
      
      if (donation.gift.postcode) {
        eventProperties['Post Code'] = donation.gift.postcode;
      }
      
      client.logEvent('recipient details provided', eventProperties);
    } catch (err) {
      console.error(err);
    }
  },

  logFormSupporter({ getters }) {
    try {
      const { product, donation } = getters;
      const eventProperties = {
        'Product ID': product.id,
        'Post Code': donation.billing.postcode,
        'Marketing opt-in': donation.optin ? 'yes' : 'no',
        'Business': donation.isBehalfBusiness ? 'yes' : 'no',
      };
      
      // Attach extra info on user for Amplitude charting
      client.setUserProperties({
        'Post Code': donation.billing.postcode, 
        'Marketing opt-in': donation.optin ? 'yes' : 'no'
      });

      client.logEvent('supporter details provided', eventProperties);
    } catch (err) {
      console.error(err);
    }
  },

  logFormPayAttempt({ getters })  {
    try {
      const { donation, product } = getters;
      const eventProperties = {
        'Product ID': product.id,
        'Donation Type': donation.type,
        'Amount': donation.amount,
        'Payment Type': paymentName[donation.payementType],
      };

      if (isHubPage() || isLeafReliefPage()) {
        eventProperties['Form Placement'] = isHubPage() ? "RA Hub" : "Leaf Relief Landing"
      }
      
      client.logEvent('payment details provided', eventProperties);
    } catch (err) {
      console.error(err);
    }
  },

  logFormPayFail({ getters }, errorMessage)  {
    try {
      const { product, donation } = getters;
      const eventProperties = {
        'Product ID': product.id,
        'Amount': donation.amount,
        'Donation Type': donation.type,
        'Payment failed type': errorMessage
      };

      if (isHubPage() || isLeafReliefPage()) {
        eventProperties['Form Placement'] = isHubPage() ? "RA Hub" : "Leaf Relief Landing"
      }
      
      client.logEvent('payment failed', eventProperties);
    } catch (err) {
      console.error(err);
    }
  },

  logFormPaySuccess({ getters }, transactionId)  {
    try {
      const { product, donation } = getters;
      const eventProperties = {
        'Product ID': product.id,
        'Amount': donation.amount,
        'Transaction ID': transactionId,
        'Donation Type': donation.type,
      };

      if (isHubPage() || isLeafReliefPage()) {
        eventProperties['Form Placement'] = isHubPage() ? "RA Hub" : "Leaf Relief Landing"
      }
      
      const revenue = new amplitude.Revenue().setProductId(product.id).setPrice(parseFloat(donation.amount)).setQuantity(1);
      client.logRevenueV2(revenue);
      client.logEvent('payment successful', eventProperties);
    } catch (err) {
      console.error(err);
    }
  },

  logDonationComplete({ getters }, paypalDonate)  {
    try {
      const { product, donation } = getters;
      const eventProperties = {
        'Product ID': paypalDonate ? paypalDonate.productid : product.id,
        'Donation Type': paypalDonate ? paypalDonate.type : donation.type,
      };
      
      client.logEvent('donation confirmation sent', eventProperties);
    } catch (err) {
      console.error(err);
    }
  },

  // AMPLITUDE LEAF RELIEF FORM ACTIONS

  logLeafBegin({ getters, dispatch }, fieldName)  {
    try {
      const { product, treeCount } = getters;
      const eventName = 'leaf relief form started';
      const eventProperties = {
        'Product ID': product.id,
        'Counter Total': treeCount,
        'Form Placement': isHubPage() ? "RA Hub" : "Leaf Relief Landing",
        'Field': fieldName
      };

      // Capture event if ssoId or amplitude not ready, will be sent once amplitude is initialized with ssoId
      if (!getters.ssoId || !getters.amplitudeId) {
        dispatch('captureEvent', { eventName, eventProperties });
        return;
      }

      client.logEvent(eventName, eventProperties);
    } catch (err) {
      console.error(err);
    }
  },

  logLeafProduct({ getters }, postcode)  {
    try {
      const { product, donation, isDonation } = getters;
      const type = isDonation ? 'Restore a forest' : 'Plant a tree';

      // Attach extra info on user for Amplitude charting
      client.setUserProperties({
        'Post Code': postcode
      });

      client.logEvent('product selected', {
        'Leaf relief type': type,
        'Amount': isDonation ? donation.amount : '',
        'Form Placement': isHubPage() ? "RA Hub" : "Leaf Relief Landing",
      });
      client.logEvent('supporter details provided', {
        'Leaf relief type': type,
        'Product ID': product.id,
        'Post Code': postcode,
        'Form Placement': isHubPage() ? "RA Hub" : "Leaf Relief Landing",
      });
    } catch (err) {
      console.error(err);
    }
  },

  logLeafComplete({ getters })  {
    try {
      const { product, isDonation, treeCount } = getters;
      const type = isDonation ? 'Restore a forest' : 'Plant a tree';
      const eventName = isDonation ? "lr donation confirmation sent" : "registration successful";
      const eventProperties = {
        'Leaf relief type': type,
        'Product ID': product.id,
        'Form Placement': isHubPage() ? "RA Hub" : "Leaf Relief Landing",
      };

      if (!isDonation) {
        eventProperties["Counter Total"] = treeCount;
      }
      
      client.logEvent(eventName, eventProperties);
    } catch (err) {
      console.error(err);
    }
  },

  // AMPLITUDE RA HUB ACTIONS

  logTabView(context, name)  {
    try {
      client.logEvent('priority tabs viewed', {
        'Tab name': name,
      });
    } catch (err) {
      console.error(err);
    }
  },
  
  logTabAction(context, { name, link })  {
    try {
      client.logEvent('priority tab actioned', {
        'Tab name': name,
        'Link ID': link,
      });
    } catch (err) {
      console.error(err);
    }
  },

  logProductLink(context, { id, link })  {
    try {
      client.logEvent("ra product link clicked", {
        "Link ID": id,
        "Destination url": link,
      });
    } catch (err) {
      console.error(err);
    }
  },

  logContentLink(context, link)  {
    try {
      client.logEvent("ra content link clicked", {
        "Destination url": link,
      });
    } catch (err) {
      console.error(err);
    }
  },

  // AMPLITUDE MISC ACTIONS

  logFaq(context, faq)  {
    try {
      client.logEvent("faqs clicked", {
        "FAQ ID": faq,
      });
    } catch (err) {
      console.error(err);
    }
  },

  logStepUp()  {
    try {
      client.logEvent("step-up viewed", {
        "Source": document.referrer,
        "Page URL": window.location.href,
      });
    } catch (err) {
      console.error(err);
    }
  },

  logStepUpActioned(context, link)  {
    try {
      client.logEvent("step-up actioned", {
        "Source": document.referrer,
        "Destination url": link,
      });
    } catch (err) {
      console.error(err);
    }
  },

  logStepUpDeclined()  {
    try {
      client.logEvent("step-up declined", {
        "Source": document.referrer,
        "Page URL": window.location.href,
      });
    } catch (err) {
      console.error(err);
    }
  },

  logPopup() {
    try {
      client.logEvent("pop-up viewed", {
        "Pop-up type": "exit intent",
        "Source": document.referrer,
        "Page URL": window.location.href,
      });
    } catch (err) {
      console.error(err);
    }
  },

  logPopupDeclined() {
    try {
      client.logEvent("pop-up declined", {
        "Pop-up type": "exit intent",
        "Source": document.referrer,
        "Page URL": window.location.href,
      });
    } catch (err) {
      console.error(err);
    }
  },

  logPopupActioned() {
    try {
      client.logEvent("pop-up cta clicked", {
        "Pop-up type": "exit intent",
        "Source": document.referrer,
        "Page URL": window.location.href,
      });
    } catch (err) {
      console.error(err);
    }
  }
};

export default {
  state,
  getters,
  actions,
  mutations
}


// WEBPACK FOOTER //
// ./src/store/modules/amplitude.js